/**
 * Dont delete any comments in this file, ever
 */

import {
  type ColumnPinningState,
  getCoreRowModel,
  type TableOptions,
  type TableState,
  type RowData as TanStackRowData,
  useReactTable,
} from "@tanstack/react-table"
import * as React from "react"
import { deepMerge } from "../../lib/utils"
import { Table } from "../ui/table"
import { DataTableBody } from "./DataTableBody"
import { DataTableHeader } from "./DataTableHeader"
import type {
  DataTableConfig,
  DataTableElements,
  DataTableHookReturn,
  DataTableScrollState,
  OptionsWithoutDataAndColumns,
  UseDataTablePlugin,
} from "./types"

/**
 * Base table hook with only 1 extra functionality over tanstack-table hook:
 * - Ability to support a plugin.
 *
 * You can use this hook for rare cases where useDataTable is not "primitive" enough for your use case
 */
export function useDataTable<TRowData extends TanStackRowData>({
  tableOptions,
  columnPinning,
  plugins = [],
}: {
  /**
   * { data, columns}
   */
  tableOptions: Pick<TableOptions<TRowData>, "data" | "columns"> &
    Partial<Omit<TableOptions<TRowData>, "data" | "columns">>
  /**
   * # 🚨🚨🚨 PLUGINS APPLIED ARE ORDER SPECIFIC, i.e. later one can overwrite slots/state/options  🚨🚨🚨
   *
   * In most cases this doesn't matter as plugins are usually made for mutually exclusive features.
   *
   * For rare exceptions, you can change order or your own meta-plugin that wraps
   * the plugins you want to apply and returns the correctly applied state.
   *
   */
  columnPinning?: ColumnPinningState
  plugins?: UseDataTablePlugin<TRowData>[]
}): DataTableHookReturn<TRowData> {
  const baseOtherOptions = {
    getCoreRowModel: getCoreRowModel(),
  } as OptionsWithoutDataAndColumns<TRowData>

  const pluginModifiedState = React.useMemo(() => {
    return plugins.reduce((acc: Partial<TableState>, plugin: UseDataTablePlugin<TRowData>) => {
      // biome-ignore lint/performance/noAccumulatingSpread: needed
      return { ...acc, ...plugin.state }
    }, {})
  }, [plugins])

  const pluginModifiedConfig = React.useMemo(() => {
    return plugins.reduce(
      (acc: Partial<DataTableConfig<TRowData>>, plugin: UseDataTablePlugin<TRowData>) => {
        // biome-ignore lint/performance/noAccumulatingSpread: needed
        return { ...acc, ...plugin.config }
      },
      {}
    )
  }, [plugins])

  const pluginModifiedOptions = React.useMemo(() => {
    return plugins.reduce(
      (
        acc: Partial<OptionsWithoutDataAndColumns<TRowData>>,
        plugin: UseDataTablePlugin<TRowData>
      ) => {
        // biome-ignore lint/performance/noAccumulatingSpread: needed
        return { ...acc, ...plugin.options }
      },
      baseOtherOptions
    )
  }, [plugins, baseOtherOptions]) as OptionsWithoutDataAndColumns<TRowData>

  const initialState = React.useMemo((): Partial<TableOptions<TRowData>> => {
    return {
      initialState: {
        ...pluginModifiedOptions.initialState,
        ...tableOptions.initialState,
        columnPinning: deepMerge(
          pluginModifiedOptions.initialState?.columnPinning,
          columnPinning,
          tableOptions.initialState?.columnPinning
        ),
      },
      enableColumnPinning: tableOptions.enableColumnPinning || Boolean(columnPinning),
    }
  }, [
    tableOptions.initialState,
    columnPinning,
    pluginModifiedOptions.initialState,
    tableOptions.enableColumnPinning,
  ])

  const state = React.useMemo(() => {
    return {
      ...pluginModifiedState,
      ...tableOptions.state,
    }
  }, [pluginModifiedState, tableOptions.state])

  const table = useReactTable({
    manualFiltering: true,
    manualPagination: true,
    /**
     * this is intentional, use useRowSelection plugin
     */
    enableRowSelection: false,
    ...tableOptions,
    data: tableOptions.data,
    columns: tableOptions.columns,
    ...pluginModifiedOptions,
    state,
    ...initialState,
  })

  // Collect all plugin components
  const elements = React.useMemo(
    () =>
      plugins.reduce<DataTableElements>((result, plugin) => {
        if (plugin.elements) {
          Object.entries(plugin.elements).forEach(([key, component]) => {
            if (!component) return
            const slotKey = key as keyof DataTableElements
            result[slotKey] = (
              <>
                {result[slotKey]}
                {component}
              </>
            )
          })
        }
        return result
      }, {} as DataTableElements),
    [plugins]
  )

  return {
    ...initialState,
    table,
    elements,
    columns: tableOptions.columns,
    config: pluginModifiedConfig,
    appliedPlugins: plugins,
  }
}

type DataTableContext<TRowData extends TanStackRowData> = ReturnType<
  typeof useDataTable<TRowData>
> & {
  wrapperRef: React.RefObject<HTMLDivElement>
}

// biome-ignore lint/suspicious/noExplicitAny: need to check later if unknown can be used
const DataTableContext = React.createContext<DataTableContext<any> | null>(null)

/**
 * Use the hook with the correct row data to create your own table-scoped components in app-specific code.
 */
export function useDataTableCtx<TRowData extends TanStackRowData>() {
  const ctx = React.useContext(DataTableContext)
  if (!ctx) {
    throw new Error("Trying to use DataTable related components outside of DataTable")
  }
  return ctx as DataTableContext<TRowData>
}

export function DataTable<TRowData extends TanStackRowData>({
  dataTable,
}: {
  dataTable: ReturnType<typeof useDataTable<TRowData>>
  plugins?: UseDataTablePlugin<TRowData>[]
}) {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)

  const [scrollState, setScrollState] = React.useState<DataTableScrollState>({
    isHorizontalScroll: false,
    isVerticalScroll: false,
    scrollHeight: 0,
    scrollLeft: 0,
    scrollTop: 0,
    scrollWidth: 0,
  })

  React.useEffect(() => {
    const element = scrollContainerRef.current

    if (!element) return

    function updateScrollState() {
      if (!element) return // Defensive check in case ref changes

      setScrollState((state) => ({
        ...state,
        scrollTop: element.scrollTop,
        scrollLeft: element.scrollLeft,
        scrollWidth: element.scrollWidth - element.clientWidth,
        scrollHeight: element.scrollHeight - element.clientHeight,
        isHorizontalScroll: element.scrollLeft > 0,
        isVerticalScroll: element.scrollTop > 0,
      }))
    }

    element.addEventListener("scroll", updateScrollState, { passive: true })
    updateScrollState()

    return () => {
      element.removeEventListener("scroll", updateScrollState)
    }
  }, [])

  return (
    <DataTableContext.Provider value={{ ...dataTable, wrapperRef: scrollContainerRef }}>
      {/* After speaking to designers and coming with a better table actions UI, we will have a "topBar" slot with IoC pattern components */}
      {/* {elements.topBar} */}
      <Table className="w-full relative z-0" scrollContainerRef={scrollContainerRef}>
        <DataTableHeader scrollState={scrollState} />
        <DataTableBody scrollState={scrollState} />
      </Table>

      {/* Render plugin bottom bar components */}
      {dataTable.elements.bottomBar}
    </DataTableContext.Provider>
  )
}

/* 
  for future ref, dont delete
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="tertiary" className="ml-auto">
        Columns <ChevronDown />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      {table
        .getAllColumns()
        .filter((column) => column.getCanHide())
        .map((column) => {
          return (
            <DropdownMenuCheckboxItem
              key={column.id}
              className="capitalize"
              checked={column.getIsVisible()}
              onCheckedChange={(value) =>
                column.toggleVisibility(!!value)
              }
            >
              {column.id}
            </DropdownMenuCheckboxItem>
          )
        })}
    </DropdownMenuContent>
  </DropdownMenu> */
